import { useEffect, useState } from "react";

import { AlertTriangle, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import {
  useAssignBucket,
  useCashBuckets,
  useRulePreview,
  type UnmappedGroup,
} from "../api";
import { fmtUsd } from "../lib";

const KIND_LABEL: Record<string, string> = {
  inflow: "Inflows",
  outflow: "Outflows",
  memo: "Memo (excluded from totals)",
};

/** Classify an unclassified vendor.
 *
 *  Two outcomes, and the distinction matters:
 *  - **Create a rule** (default) — every past and future transaction matching
 *    the pattern gets the bucket. Right for a vendor that bills repeatedly,
 *    which is nearly all of them.
 *  - **This transaction only** — a one-off override pinned to the row, which
 *    survives the weekly restatement. Right for a genuine one-off.
 *
 *  The pattern is pre-filled from the vendor key the queue grouped on, and
 *  dry-run against real bank history before saving, so an over-broad pattern
 *  shows itself here rather than by silently re-bucketing the ledger.
 */
export function ClassifyVendorDialog({
  group,
  onClose,
}: {
  group: UnmappedGroup | null;
  onClose: () => void;
}) {
  const { data: buckets } = useCashBuckets();
  const assign = useAssignBucket();

  const [bucketId, setBucketId] = useState("");
  const [makeRule, setMakeRule] = useState(true);
  const [pattern, setPattern] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (group) {
      setBucketId("");
      setMakeRule(true);
      setPattern(group.suggested_pattern);
      setReason("");
      assign.reset();
    }
    // Re-seeding is keyed on the row being opened, not on the mutation object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  const preview = useRulePreview(pattern, Boolean(group) && makeRule);
  const grouped = (buckets ?? []).reduce<Record<string, typeof buckets>>((acc, b) => {
    (acc[b.kind] ??= []).push(b);
    return acc;
  }, {});

  const canSave = Boolean(bucketId) && (!makeRule || (pattern.trim() && preview.data?.valid));

  const save = () => {
    if (!group || !canSave) return;
    assign.mutate(
      {
        transaction_id: group.sample_transaction_id,
        bucket_id: bucketId,
        reason: reason.trim() || null,
        create_rule_pattern: makeRule ? pattern.trim() : null,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open={Boolean(group)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Classify vendor</DialogTitle>
          <DialogDescription>
            {group && (
              <>
                {group.count} transaction{group.count === 1 ? "" : "s"} totalling{" "}
                {fmtUsd(group.total_amount)}, first seen {group.first_seen}.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="rounded-md border bg-muted/30 p-3">
            <p className="break-all font-mono text-xs text-muted-foreground">
              {group?.description}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="classify-bucket">Cash bucket</Label>
            <Select value={bucketId} onValueChange={setBucketId}>
              <SelectTrigger id="classify-bucket">
                <SelectValue placeholder="Pick a bucket…" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(grouped).map(([kind, items]) => (
                  <div key={kind}>
                    <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                      {KIND_LABEL[kind] ?? kind}
                    </p>
                    {(items ?? []).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start gap-3 rounded-md border p-3">
            <Switch id="classify-rule" checked={makeRule} onCheckedChange={setMakeRule} />
            <div className="flex-1">
              <Label htmlFor="classify-rule" className="cursor-pointer">
                Create a mapping rule
              </Label>
              <p className="text-xs text-muted-foreground">
                {makeRule
                  ? "Every matching transaction, past and future, gets this bucket. Right for a vendor that bills repeatedly."
                  : "Pins this one transaction only. The override survives the weekly re-import."}
              </p>
            </div>
          </div>

          {makeRule && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="classify-pattern">Pattern (regex, case-insensitive)</Label>
              <Input
                id="classify-pattern"
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                className="font-mono text-xs"
              />
              {preview.data && !preview.data.valid && (
                <p className="text-sm text-danger">Invalid pattern: {preview.data.error}</p>
              )}
              {preview.data?.valid && (
                <div className="flex flex-col gap-1 text-xs">
                  <p className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <Check className="h-3.5 w-3.5 text-success" />
                    Matches <b className="text-foreground">{preview.data.match_count}</b>{" "}
                    transaction{preview.data.match_count === 1 ? "" : "s"} in bank history.
                  </p>
                  {preview.data.already_classified > 0 && (
                    <p className="inline-flex items-center gap-1.5 text-warning">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {preview.data.already_classified} of those already have a bucket from
                      another rule — this pattern may be broader than you intend.
                    </p>
                  )}
                  {preview.data.shadowed_by && (
                    <p className="inline-flex items-start gap-1.5 text-muted-foreground">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                      <span>
                        An existing rule{" "}
                        <code className="rounded bg-muted px-1">{preview.data.shadowed_by}</code>{" "}
                        currently claims these. Rules are first-match-wins, so this one will be
                        placed <b className="text-foreground">ahead of it</b> — otherwise it
                        would never fire.
                      </span>
                    </p>
                  )}
                  {preview.data.samples.length > 0 && (
                    <ul className="mt-1 flex flex-col gap-0.5 text-muted-foreground">
                      {preview.data.samples.slice(0, 4).map((s) => (
                        <li key={s} className="truncate font-mono" title={s}>
                          {s}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="classify-reason">Why (optional)</Label>
            <Textarea
              id="classify-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. Cody confirmed — marketing consultant"
            />
            <p className="text-xs text-muted-foreground">
              Recorded against the rule and in the audit trail, so the next person can see who
              decided this and when.
            </p>
          </div>

          {assign.isError && (
            <p className="text-sm text-danger">Failed: {assign.error.message}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canSave || assign.isPending} onClick={save}>
            {assign.isPending ? "Saving…" : makeRule ? "Create rule & reclassify" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
